import React from 'react';

import Icon from 'core/modules/atoms/Icon';
import SelectCustomizable from 'core/modules/atoms/SelectCustomizable';
import AreaGraphic from 'core/modules/components/charts/AreaGraphic/AreaGraphic';

import './_tutor-usage-section.scss';
import useTutorUsageSection from './useTutorUsageSection';

const CHART_COLORS = ['#FF7450', '#E3366F'];
const CHART_RANGE_VERTICAL = [0, 25];
const CHART_HEIGHT = 250;

/**
 * TutorUsageSection - Displays real-time usage stats and weekly activity chart.
 * Currently only works with mock data (no real API yet).
 */
function TutorUsageSection(): JSX.Element {
  const { dailyActivity, isMockMode, stats, t } = useTutorUsageSection();

  const interactionsLabel = t('chatbot:tutor_usage_legend_interactions');
  const questionsLabel = t('chatbot:tutor_usage_legend_questions');
  const chartLines = [{ text: interactionsLabel }, { text: questionsLabel }];
  const chartData = dailyActivity?.map((d) => ({
    name: d.name,
    [interactionsLabel]: d.interactions,
    [questionsLabel]: d.questions,
  }));

  const allModesOption = { label: t('chatbot:tutor_usage_all_modes'), value: 'all' };

  if (!isMockMode) {
    return (
      <div className="tutor-usage-section">
        <p className="tutor-usage-section__empty">{t('chatbot:tutor_usage_enable_demo')}</p>
      </div>
    );
  }

  if (!stats || !dailyActivity) {
    return <div className="tutor-usage-section" />;
  }

  return (
    <div className="tutor-usage-section">
      <h2 className="tutor-usage-section__title">{t('chatbot:tutor_usage_realtime_title')}</h2>

      <ul className="tutor-usage-section__stats">
        <li className="tutor-usage-section__stat-card">
          <div className="tutor-usage-section__stat-header">
            <div className="tutor-usage-section__stat-icon tutor-usage-section__stat-icon--purple">
              <Icon color="default" size="default" type="students" />
            </div>
          </div>
          <p className="tutor-usage-section__stat-label">{t('chatbot:tutor_usage_active_students')}</p>
          <p className="tutor-usage-section__stat-value">
            {stats.activeStudents.active}/{stats.activeStudents.total}
          </p>
          <p className="tutor-usage-section__stat-subtitle">
            {t('chatbot:tutor_usage_active_students_subtitle', { percentage: stats.activeStudents.percentage })}
          </p>
          <p className="tutor-usage-section__stat-trend tutor-usage-section__stat-trend--positive">
            <Icon color="success" size="xs" type="trend-up" />+{stats.activeStudents.trend}%
            <span className="tutor-usage-section__stat-trend-text">{t('chatbot:tutor_usage_trend_previous_week')}</span>
          </p>
        </li>

        <li className="tutor-usage-section__stat-card">
          <div className="tutor-usage-section__stat-header">
            <div className="tutor-usage-section__stat-icon tutor-usage-section__stat-icon--blue">
              <Icon color="default" size="default" type="comment" />
            </div>
          </div>
          <p className="tutor-usage-section__stat-label">{t('chatbot:tutor_usage_total_interactions')}</p>
          <p className="tutor-usage-section__stat-value">{stats.totalInteractions.count}</p>
          <p className="tutor-usage-section__stat-subtitle">
            {t('chatbot:tutor_usage_total_interactions_subtitle', { avg: stats.totalInteractions.avgPerStudent })}
          </p>
          <p className="tutor-usage-section__stat-trend tutor-usage-section__stat-trend--positive">
            <Icon color="success" size="xs" type="trend-up" />+{stats.totalInteractions.trend}%
            <span className="tutor-usage-section__stat-trend-text">{t('chatbot:tutor_usage_trend_previous_week')}</span>
          </p>
        </li>

        <li className="tutor-usage-section__stat-card">
          <div className="tutor-usage-section__stat-header">
            <div className="tutor-usage-section__stat-icon tutor-usage-section__stat-icon--orange">
              <Icon color="default" size="default" type="book-open" />
            </div>
            <div className="tutor-usage-section__stat-info-icon">
              <Icon color="grey-03" size="default" type="info-circle" />
            </div>
          </div>
          <p className="tutor-usage-section__stat-label">{t('chatbot:tutor_usage_study_mode')}</p>
          <p className="tutor-usage-section__stat-value">{stats.studyModeUsage.percentage}%</p>
          <p className="tutor-usage-section__stat-subtitle">
            {t('chatbot:tutor_usage_practice_mode_subtitle', { percentage: stats.studyModeUsage.practicePercentage })}
          </p>
          <p className="tutor-usage-section__stat-trend tutor-usage-section__stat-trend--negative">
            <Icon color="red" size="xs" type="trend-down" />
            {stats.studyModeUsage.trend}%<span className="tutor-usage-section__stat-trend-text">{t('chatbot:tutor_usage_trend_previous_week')}</span>
          </p>
        </li>

        <li className="tutor-usage-section__stat-card">
          <div className="tutor-usage-section__stat-header">
            <div className="tutor-usage-section__stat-icon tutor-usage-section__stat-icon--green">
              <Icon color="default" size="default" type="success" />
            </div>
          </div>
          <p className="tutor-usage-section__stat-label">{t('chatbot:tutor_usage_resolution_rate')}</p>
          <p className="tutor-usage-section__stat-value">{stats.resolutionRate.percentage}%</p>
          <p className="tutor-usage-section__stat-subtitle">{t('chatbot:tutor_usage_resolution_subtitle')}</p>
          <p className="tutor-usage-section__stat-trend tutor-usage-section__stat-trend--positive">
            <Icon color="success" size="xs" type="trend-up" />+{stats.resolutionRate.trend}%
            <span className="tutor-usage-section__stat-trend-text">{t('chatbot:tutor_usage_trend_previous_week')}</span>
          </p>
        </li>
      </ul>

      <div className="tutor-usage-section__chart">
        <div className="tutor-usage-section__chart-header">
          <div className="tutor-usage-section__chart-header-text">
            <h3 className="tutor-usage-section__chart-title">{t('chatbot:tutor_usage_activity_title')}</h3>
            <p className="tutor-usage-section__chart-description">{t('chatbot:tutor_usage_activity_description')}</p>
          </div>
          <div className="tutor-usage-section__chart-filter">
            <SelectCustomizable onChange={() => {}} options={[allModesOption]} size="default" value={allModesOption} variant="default" />
          </div>
        </div>

        <div className="tutor-usage-section__chart-container">
          <AreaGraphic
            colorsCharts={CHART_COLORS}
            data={chartData}
            height={CHART_HEIGHT}
            lines={chartLines}
            lineStrokeWidth={2}
            rangeVertical={CHART_RANGE_VERTICAL}
            showXAxis
            showYAxis
            typeLine="monotone"
            width="100%"
          />
        </div>

        <ul className="tutor-usage-section__chart-legend">
          <li className="tutor-usage-section__chart-legend-item">
            <span className="tutor-usage-section__chart-legend-color" style={{ backgroundColor: CHART_COLORS[0] }} />
            {t('chatbot:tutor_usage_legend_interactions')}
          </li>
          <li className="tutor-usage-section__chart-legend-item">
            <span className="tutor-usage-section__chart-legend-color" style={{ backgroundColor: CHART_COLORS[1] }} />
            {t('chatbot:tutor_usage_legend_questions')}
          </li>
        </ul>
      </div>
    </div>
  );
}

export default TutorUsageSection;
